/**
 * Well Departure Reminder Component
 * 
 * Displays a notification popup when a user leaves a well
 * without entering production data, with quick action to log entry.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Clock, X, ClipboardEdit, BellOff } from 'lucide-react';
import { Button } from '../ui/button';

interface WellDepartureReminderProps {
  isVisible: boolean;
  wellId: string;
  wellName: string;
  dwellDurationMs: number;
  onLogEntry: () => void;
  onDismiss: () => void;
  onSnooze?: () => void;
  isDarkMode?: boolean;
}

export function WellDepartureReminder({
  isVisible,
  wellId,
  wellName,
  dwellDurationMs,
  onLogEntry,
  onDismiss,
  onSnooze,
  isDarkMode = false,
}: WellDepartureReminderProps) {
  const formatDuration = (ms: number): string => {
    const minutes = Math.round(ms / 60000);
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
            onClick={onDismiss}
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`
              fixed bottom-4 left-4 right-4 z-[9999] mx-auto max-w-md
              rounded-2xl shadow-2xl border overflow-hidden
              ${isDarkMode 
                ? 'bg-slate-900 border-slate-700' 
                : 'bg-white border-gray-200'
              }
            `}
          >
            {/* Header accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" />
            
            {/* Content */}
            <div className="p-4">
              {/* Title row */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`
                    w-12 h-12 rounded-xl flex items-center justify-center
                    ${isDarkMode 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'bg-amber-100 text-amber-600'
                    }
                  `}>
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Missing Entry
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Production data reminder
                    </p>
                  </div>
                </div>
                <button
                  onClick={onDismiss}
                  className={`
                    p-2 rounded-lg transition-colors
                    ${isDarkMode 
                      ? 'text-gray-400 hover:bg-slate-800' 
                      : 'text-gray-400 hover:bg-gray-100'
                    }
                  `}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Well info */}
              <div className={`
                p-3 rounded-xl mb-4
                ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}
              `}>
                <p className={`font-semibold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {wellName}
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    Time at well: {formatDuration(dwellDurationMs)}
                  </span>
                </div>
              </div>

              {/* Message */}
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                You left this well without logging production data. Would you like to enter data now?
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={onLogEntry}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white h-12 text-base font-semibold"
                >
                  <ClipboardEdit className="w-5 h-5 mr-2" />
                  Log Entry Now
                </Button>
                <Button
                  variant="outline"
                  onClick={onDismiss}
                  className={`
                    h-12 px-4
                    ${isDarkMode 
                      ? 'border-slate-600 text-gray-300 hover:bg-slate-800' 
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  <BellOff className="w-5 h-5" />
                </Button>
              </div>

              {/* Small text hint */}
              <p className={`
                text-xs text-center mt-3
                ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}
              `}>
                Manage reminders in Settings → Notifications
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default WellDepartureReminder;
